import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { ApiError, apiFetch } from '../lib/apiClient';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';

export type IncidentSeverityTarget = 'Informational' | 'Low' | 'Medium' | 'High';

export interface IncidentSeverityPayload {
  severity: IncidentSeverityTarget;
  previousSeverity?: string;
  severityComment?: string;
}

interface Props {
  incident: Incident;
  customerId?: string;
  initialSeverity?: IncidentSeverityTarget;
  onCancel: () => void;
  onUpdated: (payload: IncidentSeverityPayload) => void;
}

const SEVERITIES: { value: IncidentSeverityTarget; label: string; description: string; className: string }[] = [
  { value: 'High', label: 'High', description: 'Requires immediate analyst attention.', className: 'severity-high' },
  { value: 'Medium', label: 'Medium', description: 'Important but not immediately critical.', className: 'severity-medium' },
  { value: 'Low', label: 'Low', description: 'Low-impact or lower-priority investigation.', className: 'severity-low' },
  { value: 'Informational', label: 'Informational', description: 'Informational / awareness only.', className: 'severity-informational' },
];

function normalizeSeverity(value: string | undefined): IncidentSeverityTarget {
  const s = String(value || '').toLowerCase();
  if (s === 'high' || s === 'critical') return 'High';
  if (s === 'medium') return 'Medium';
  if (s === 'low') return 'Low';
  return 'Informational';
}

export function IncidentSeverityDialog({ incident, customerId, initialSeverity, onCancel, onUpdated }: Props) {
  const currentSeverity = normalizeSeverity(incident.severity);
  const defaultSeverity = useMemo<IncidentSeverityTarget>(() => currentSeverity, [currentSeverity]);
  const [targetSeverity, setTargetSeverity] = useState<IncidentSeverityTarget>(initialSeverity ?? defaultSeverity);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackPrompt, setFallbackPrompt] = useState<string | null>(null);
  const [fallbackAllowed, setFallbackAllowed] = useState(false);

  function isDelegatedFallbackPrompt(e: unknown): e is ApiError {
    return e instanceof ApiError && e.status === 409 && e.body?.['error'] === 'delegated_rbac_required';
  }

  async function submitSeverity(useServicePrincipalFallback = false) {
    setLoading(true);
    setError(null);
    if (!useServicePrincipalFallback) { setFallbackPrompt(null); setFallbackAllowed(false); }

    const payload: IncidentSeverityPayload = {
      severity: targetSeverity,
      previousSeverity: incident.severity,
      severityComment: comment.trim(),
    };

    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/severity${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      void response;
      notifyIncidentExecutionUpdated(incident.id);
      onUpdated(payload);
    } catch (e) {
      if (isDelegatedFallbackPrompt(e)) {
        const msg = typeof e.body['message'] === 'string'
          ? e.body['message'] as string
          : 'Your user identity could not complete this delegated Sentinel action. Do you still wish to proceed using the Service Principal RBAC?';
        setFallbackPrompt(msg);
        setFallbackAllowed(e.body?.['canUseServicePrincipalFallback'] === true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="mb-5">
            <h2 className="text-base font-bold text-foreground mb-1">Change Incident Severity</h2>
            <p className="text-xs text-muted-foreground font-mono">
              #{incident.incidentNumber} · {incident.title.length > 60 ? incident.title.slice(0, 60) + '…' : incident.title}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Current severity: <span className="font-semibold">{incident.severity}</span>
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-foreground mb-2">
              New severity <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {SEVERITIES.map(sev => (
                <label
                  key={sev.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    targetSeverity === sev.value
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="targetSeverity"
                    checked={targetSeverity === sev.value}
                    onChange={() => setTargetSeverity(sev.value)}
                    className="accent-primary mt-1"
                  />
                  <span className="flex-1 min-w-0">
                    <span className={`inline-flex text-xs font-semibold px-2 py-1 rounded ${sev.className}`}>{sev.label}</span>
                    <span className="block text-[11px] text-muted-foreground mt-1">{sev.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-amber-600 dark:text-amber-300 mt-2">
              Severity updates require the <span className="font-semibold">incidents.change_severity</span> capability and Sentinel write RBAC.
            </p>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-foreground mb-2">
              Analyst note <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add a reason for this severity change…"
              rows={3}
              className="w-full rounded-lg border border-border bg-background text-sm text-foreground px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/60"
            />
          </div>

          {fallbackPrompt && (
            <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3">
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-1">Delegated user action failed</div>
              <div className="text-xs text-amber-800 dark:text-amber-100 mb-3">{fallbackPrompt}</div>
              <div className="flex gap-2">
                {fallbackAllowed && (
                  <button
                    type="button"
                    onClick={() => submitSeverity(true)}
                    disabled={loading}
                    className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    Yes, use Service Principal
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setFallbackPrompt(null); setFallbackAllowed(false); }}
                  disabled={loading}
                  className="px-3 py-1.5 rounded border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                >
                  No, cancel
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => submitSeverity(false)}
              disabled={loading || targetSeverity === currentSeverity}
              className="flex-1 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                  Updating…
                </>
              ) : 'Update Severity'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
